import { useGraphQLQuery } from '@opengov/bnp-core';
import { IStoryFolder as ISharedStoryFolder } from '@opengov/stories-shared/types';
import { buildPublicationFolderHierarchyMap } from '@opengov/stories-shared/utils/general';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  GetFolderHierarchyWithStoriesForOnlinePublicationQuery,
  GetFolderHierarchyWithStoriesForOnlinePublicationQueryVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { IStoryFolder } from '../../../../../types';
import { useToast } from '../../../Toast/context/useToast.context';
import { groupAllStoryFolders } from '../../../../utils';

import {
  FolderHierarchyWithStoriesKeyParams,
  setupOnlinePublicationQueryKeys
} from './setupOnlinePublicationQueryKeys';

const GetFolderHierarchyWithStoriesForOnlinePublicationDocument = graphql(`
  query GetFolderHierarchyWithStoriesForOnlinePublication($publicationId: uuid!, $entityId: Int!) {
    publicationRootFolder: storyFolders(
      where: {
        publicationId: { _eq: $publicationId }
        entityId: { _eq: $entityId }
        parentFolder: { parentFolderId: { _is_null: true } }
      }
    ) {
      id
    }
    publicationFolders: storyFolders(
      where: {
        publicationId: { _eq: $publicationId }
        entityId: { _eq: $entityId }
        deletedAt: { _is_null: true }
      }
    ) {
      id
      title
      parentFolderId
      stories(where: { deletedAt: { _is_null: true } }, order_by: { order: asc }) {
        id: _id
        title
        state
        order
      }
    }
  }
`);

export type UseFolderHierarchyWithStoriesParams = FolderHierarchyWithStoriesKeyParams | null;

export function useFolderHierarchyWithStories(
  params: UseFolderHierarchyWithStoriesParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useGraphQLQuery<
    GetFolderHierarchyWithStoriesForOnlinePublicationQuery,
    GetFolderHierarchyWithStoriesForOnlinePublicationQueryVariables,
    IStoryFolder[]
  >({
    queryKey: params
      ? setupOnlinePublicationQueryKeys.folderHierarchyWithStories(params)
      : ([...setupOnlinePublicationQueryKeys.all, 'folderHierarchyWithStories', 'disabled'] as const),
    document: GetFolderHierarchyWithStoriesForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL,
    variables: params
      ? { publicationId: params.publicationId, entityId: params.entityId }
      : { publicationId: '', entityId: 0 },
    options: {
      enabled: params !== null,
      onError: () => {
        toggleToast({ title: errorMessage, severity: 'error' });
      },
      // Mirrors the original effect's body: builds the section hierarchy from
      // the root folder's subFolders and feeds it through `groupAllStoryFolders`
      // so consumers receive the same shape the legacy `effects.getPublicationFoldersHierarchyWithStories`
      // produced.
      // `buildPublicationFolderHierarchyMap` is typed against the
      // stories-shared `IStoryFolder`, while `groupAllStoryFolders` (and
      // every UI consumer of this query) uses the stories-app local
      // `IStoryFolder`. Their runtime shapes are compatible — only the
      // type-level definitions differ — so we cast at the boundary.
      select: (data: GetFolderHierarchyWithStoriesForOnlinePublicationQuery): IStoryFolder[] => {
        const rootFolder = data.publicationRootFolder?.[0];
        const folders = data.publicationFolders;
        if (!rootFolder || !folders) return [];
        const hierarchyMap = buildPublicationFolderHierarchyMap(folders as unknown as ISharedStoryFolder[]);
        const root = hierarchyMap[rootFolder.id];
        const sections = (root?.subFolders ?? []) as unknown as IStoryFolder[];
        return groupAllStoryFolders(sections.map((section) => ({ storyFolder: section })));
      }
    }
  });
}
